import { useEffect, useRef } from "react";
import { createScene, SCENE_OPTIONS } from "./scenes";

export const name = "Swiss Viz";
export const exportOptions = ["png", "copy", "webm"];

export const variables = [
  {
    label: "Scene",
    name: "scene",
    type: "select",
    attributes: { options: SCENE_OPTIONS },
  },
  {
    label: "Speed",
    name: "speed",
    type: "range",
    attributes: { range: [0, 200] },
  },
  {
    label: "Scale",
    name: "scale",
    type: "range",
    attributes: { range: [10, 30] },
  },
  {
    label: "Dot Grid",
    name: "grid",
    type: "toggle",
  },
  {
    label: "Duration (s)",
    name: "duration",
    type: "range",
    attributes: { range: [1, 30] },
  },
];

export const presets = [
  {
    name: "Oscillation",
    scene: "Oscillation",
    speed: 100,
    scale: 10,
    grid: true,
    duration: 10,
  },
  {
    name: "Venn",
    scene: "Venn",
    speed: 100,
    scale: 10,
    grid: true,
    duration: 20,
  },
];

// ─── Dot grid ─────────────────────────────────────────────────────────────────
// Pre-rendered once per canvas size and composited on top each frame.
// Intentionally separate from the scene system — the grid never scales.

const BACKGROUND_COLOR = "#000";
const GRID_PADDING = 8;
const GRID_RADIUS = 1;
const GRID_MARGIN = 24;

function buildDotGrid(w, h) {
  const dc = document.createElement("canvas");
  dc.width = w;
  dc.height = h;
  const dctx = dc.getContext("2d");
  dctx.fillStyle = BACKGROUND_COLOR;
  for (
    let x = GRID_MARGIN + GRID_PADDING * 0.5;
    x < w - GRID_MARGIN;
    x += GRID_PADDING
  ) {
    for (
      let y = GRID_MARGIN + GRID_PADDING * 0.5;
      y < h - GRID_MARGIN;
      y += GRID_PADDING
    ) {
      dctx.beginPath();
      dctx.arc(x, y, GRID_RADIUS, 0, Math.PI * 2);
      dctx.fill();
    }
  }
  return dc;
}

// ─── Output ───────────────────────────────────────────────────────────────────

export const Output = ({ dimensions, variables, recordingRef }) => {
  const { width, height } = dimensions;
  const canvasRef = useRef(null);

  // Animation clock — stored in refs so the scene effect can reset them
  // without restarting the RAF loop.
  const sceneRef = useRef(null);
  const totalTimeRef = useRef(0);
  const lastTRef = useRef(null);

  // Variable refs — written on change, read each frame, no restart needed.
  const speedRef = useRef(variables.speed / 100);
  useEffect(() => {
    speedRef.current = variables.speed / 100;
  }, [variables.speed]);

  const scaleRef = useRef(variables.scale / 10);
  useEffect(() => {
    scaleRef.current = variables.scale / 10;
  }, [variables.scale]);

  const gridRef = useRef(variables.grid);
  useEffect(() => {
    gridRef.current = variables.grid;
  }, [variables.grid]);

  // ── Scene lifecycle ───────────────────────────────────────────────────────
  // Declared before the RAF effect so it runs first on shared triggers
  // (mount, dimension change), guaranteeing sceneRef is populated before
  // the first draw call.
  //
  // Resetting the clock on scene change means finite scenes like Venn always
  // play from their beginning. Resetting on dimension change is necessary
  // because scene positions are computed relative to w/h.
  useEffect(() => {
    sceneRef.current = createScene(variables.scene, width, height);
    totalTimeRef.current = 0;
    lastTRef.current = null;
  }, [variables.scene, width, height]);

  // ── Animation loop ────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const w = width;
    const h = height;
    const dotGrid = buildDotGrid(w, h);

    let animId;

    function draw(t) {
      const recording = recordingRef?.current;

      // Time advancement: exact fixed step during recording (smooth video),
      // real elapsed time otherwise.
      if (recording) {
        totalTimeRef.current += recording.frameDt;
      } else {
        if (lastTRef.current !== null) {
          totalTimeRef.current += (t - lastTRef.current) * speedRef.current;
        }
      }
      lastTRef.current = t;

      // Background
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = BACKGROUND_COLOR;
      ctx.fillRect(0, 0, w, h);

      // Circles — world-space positions from the active scene, then scaled
      // by the viewport zoom (Scale variable) centred on the canvas midpoint.
      const circles = sceneRef.current?.getCircles(totalTimeRef.current) ?? [];
      const scale = scaleRef.current;

      ctx.globalCompositeOperation = "screen";
      for (const { r, g, b, cx: wCx, cy: wCy, radius: wR } of circles) {
        const cx = w * 0.5 + (wCx - w * 0.5) * scale;
        const cy = h * 0.5 + (wCy - h * 0.5) * scale;
        const radius = wR * scale;

        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
        grad.addColorStop(0, `rgba(${r},${g},${b},1)`);
        grad.addColorStop(0.8, `rgba(${r},${g},${b},0.8)`);
        grad.addColorStop(1, `rgba(${r},${g},${b},0)`);

        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.fill();
      }

      // Dot grid — always at fixed canvas dimensions, never scaled
      if (gridRef.current) {
        ctx.globalCompositeOperation = "source-over";
        ctx.drawImage(dotGrid, 0, 0);
      }

      recording?.onFrame();
      animId = requestAnimationFrame(draw);
    }

    animId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animId);
  }, [width, height, recordingRef]);

  return <canvas ref={canvasRef} width={width} height={height} />;
};
